import {
  PERMISSION_ACTION_KEYS,
  VIEW_ONLY_AREA_KEYS,
  type FeatureKey,
  type PermissionAction,
  type PermissionArea,
  type PermissionScope,
} from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";

// Le etichette del catalogo dei permessi (piano M1b Task 8), dai mock-up `permessi.html`. Ogni mappa è
// tipizzata sulle chiavi dei contratti (`PERMISSION_AREA_KEYS`, …, `FEATURE_KEYS_CATALOG`): una voce del
// catalogo senza etichetta non compila, e il test di parità del server fa fallire la CI se i contratti e
// `@gioia365/db` si separano. I testi stanno nei cataloghi `permissions.*`, in italiano e in inglese.

export const AREA_LABEL: Record<PermissionArea, MessageKey> = {
  leads: "permissions.area.leads",
  smart_lists: "permissions.area.smart_lists",
  tasks: "permissions.area.tasks",
  opportunities: "permissions.area.opportunities",
  conversations: "permissions.area.conversations",
  activity: "permissions.area.activity",
  reports: "permissions.area.reports",
};

export const ACTION_LABEL: Record<PermissionAction, MessageKey> = {
  view: "permissions.action.view",
  create: "permissions.action.create",
  update: "permissions.action.update",
  delete: "permissions.action.delete",
};

export const SCOPE_LABEL: Record<PermissionScope, MessageKey> = {
  none: "permissions.scope.none",
  own: "permissions.scope.own",
  all: "permissions.scope.all",
};

/** «Creare» non ha un ambito «solo i suoi»: soltanto «No» e «Sì». */
export const CREATE_SCOPE_LABEL: Record<Exclude<PermissionScope, "own">, MessageKey> = {
  none: "permissions.createScope.none",
  all: "permissions.createScope.all",
};

/** Le azioni che un'area offre: le aree di sola lettura soltanto «vedere». */
export function areaActions(area: PermissionArea): readonly PermissionAction[] {
  return (VIEW_ONLY_AREA_KEYS as readonly string[]).includes(area) ? ["view"] : PERMISSION_ACTION_KEYS;
}

/** I gruppi dei mock-up: funzioni speciali per tema e, in fondo, le sezioni delle Impostazioni. */
export const FEATURE_GROUPS: readonly { title: MessageKey; keys: readonly FeatureKey[] }[] = [
  {
    title: "permissions.group.leads",
    keys: ["change_owner", "import_leads", "export_leads", "bulk_actions", "merge_duplicates", "trash_restore", "manage_tags"],
  },
  { title: "permissions.group.calls", keys: ["call", "whatsapp_send", "listen_team_recordings", "commercial_view"] },
  { title: "permissions.group.team", keys: ["manage_team", "manage_roles"] },
  {
    title: "permissions.group.settings",
    keys: [
      "settings.team",
      "settings.roles",
      "settings.phone_numbers",
      "settings.lead_statuses",
      "settings.lead_fields",
      "settings.lead_visibility",
      "settings.tags",
      "settings.call_sequence",
      "settings.outcomes",
      "settings.callbacks",
      "settings.whatsapp_channels",
      "settings.whatsapp_sending",
      "settings.whatsapp_templates",
      "settings.calendar",
      "settings.assignment",
      "settings.lead_intake",
      "settings.table_views",
      "settings.goals",
    ],
  },
];

export const FEATURE_LABEL: Record<FeatureKey, MessageKey> = {
  change_owner: "permissions.feature.change_owner",
  import_leads: "permissions.feature.import_leads",
  export_leads: "permissions.feature.export_leads",
  bulk_actions: "permissions.feature.bulk_actions",
  merge_duplicates: "permissions.feature.merge_duplicates",
  trash_restore: "permissions.feature.trash_restore",
  manage_tags: "permissions.feature.manage_tags",
  call: "permissions.feature.call",
  whatsapp_send: "permissions.feature.whatsapp_send",
  listen_team_recordings: "permissions.feature.listen_team_recordings",
  commercial_view: "permissions.feature.commercial_view",
  manage_team: "permissions.feature.manage_team",
  manage_roles: "permissions.feature.manage_roles",
  "settings.team": "permissions.feature.settings.team",
  "settings.roles": "permissions.feature.settings.roles",
  "settings.phone_numbers": "permissions.feature.settings.phone_numbers",
  "settings.lead_statuses": "permissions.feature.settings.lead_statuses",
  "settings.lead_fields": "permissions.feature.settings.lead_fields",
  "settings.lead_visibility": "permissions.feature.settings.lead_visibility",
  "settings.tags": "permissions.feature.settings.tags",
  "settings.call_sequence": "permissions.feature.settings.call_sequence",
  "settings.outcomes": "permissions.feature.settings.outcomes",
  "settings.callbacks": "permissions.feature.settings.callbacks",
  "settings.whatsapp_channels": "permissions.feature.settings.whatsapp_channels",
  "settings.whatsapp_sending": "permissions.feature.settings.whatsapp_sending",
  "settings.whatsapp_templates": "permissions.feature.settings.whatsapp_templates",
  "settings.calendar": "permissions.feature.settings.calendar",
  "settings.assignment": "permissions.feature.settings.assignment",
  "settings.lead_intake": "permissions.feature.settings.lead_intake",
  "settings.table_views": "permissions.feature.settings.table_views",
  "settings.goals": "permissions.feature.settings.goals",
};
